import React, { useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import { notBlank } from "../common/general";

import FileRow from "./FileRow";
import { Typography } from "@material-ui/core";

export const parseExtension = name =>
  name.match(/\.[^.]*$|$/i)[0].toLowerCase();

export const acceptRegex = accept =>
  new RegExp(
    "^(" + accept.replace(/[, ]+/g, "|").replace(/\/\*/g, "/.*") + ")$",
    "i"
  );

export const isValidType = (accept, type) =>
  !accept || acceptRegex(accept).test(type);

export const isValidExtension = (accept, extension) =>
  !accept || acceptRegex(accept).test(extension);

export const isValidAccept = (accept, file) =>
  !accept ||
  (notBlank(file) &&
    (isValidType(accept, file.type) ||
      isValidExtension(accept, parseExtension(file.name))));

export const isValidMaxSize = (maxSize, size) => {
  if (!maxSize && typeof maxSize !== "number") {
    return true;
  }

  const maxSizeNum = parseInt(maxSize, 10);
  return isNaN(maxSizeNum) ? false : size <= maxSizeNum;
};

const stopEvent = e => {
  e.stopPropagation();
  e.preventDefault();
};

const initialState = {
  draggedOver: false,
};

const useStyles = makeStyles(theme => ({
  container: {
    position: "relative",
  },

  button: {
    width: "100%",
  },

  buttonLabel: {
    display: "block",
    textAlign: "center",
  },

  dropZone: {
    position: "relative",
    borderWidth: 1,
    borderStyle: "dashed",
    borderColor: theme.palette.primary.main,
    borderRadius: 3,
    overflow: "hidden",
    padding: 14,
    display: "flex",
    flexDirection: "column",
  },

  dropZoneDraggedOver: {
    position: "relative",
    borderWidth: 1,
    borderStyle: "solid",
    borderColor: theme.palette.primary.dark,
    borderRadius: 3,
    overflow: "hidden",
    padding: 14,
    display: "flex",
    flexDirection: "column",
  },

  dropZoneError: {
    borderColor: theme.palette.primary.error,
  },

  selectFileRow: {
    display: "flex",
    justifyContent: "center",
  },

  errorLabel: {
    color: theme.palette.primary.error,
    fontSize: 12,
    margin: "10px 0",
  },

  uploadIcon: {
    color: theme.palette.secondary.main,
    marginLeft: 20,
  },

  dropHere: {
    color: theme.palette.secondary.main,
    marginLeft: 8,
    fontSize: 14,
  },

  fileInput: {
    display: "none",
  },

  fileTypes: {
    fontStyle: "italic",
  },

  closeButton: {
    cursor: "pointer",
  },

  visuallyHidden: {
    position: "absolute",
    width: 1,
    height: 1,
    margin: -1,
    padding: 0,
    overflow: "hidden",
    clip: "rect(0, 0, 0, 0)",
    border: 0,
    whiteSpace: "nowrap" /* Prevents text from wrapping */,

    "&:focus + button": {
      outline: "2px solid #0053ba",
      outlineOffset: 2,
    },
  },
}));

const FileUploadInput = props => {
  const classes = useStyles();

  const [refCount, setRefCount] = useState(0);
  const [fileInput, setFileInput] = useState(null);
  const [state, setState] = useState(initialState);

  useEffect(() => {
    if (refCount === 1) {
      setState({
        draggedOver: true,
      });
    } else if (refCount === 0) {
      setState(initialState);
    }
  }, [refCount]);

  const fileChanged = files => {
    Array.from(files).forEach(file => {
      if (!isValidAccept(props.accept, file)) {
        return;
      }

      if (!isValidMaxSize(props.maxSize, file.size)) {
        return;
      }
    });

    if (props.onChange) {
      props.onChange(files);
    }
  };

  const fileRemoved = file => {
    if (props.onRemove) {
      props.onRemove(file);
    } else if (props.onChange) {
      props.onChange(file);
    }
  };

  const dragHandler = handler => {
    return e => {
      stopEvent(e);

      if (handler) {
        handler(e);
      }
    };
  };

  const getDropZoneClassName = () => {
    return state.draggedOver ? classes.dropZoneDraggedOver : classes.dropZone;
  };

  return (
    <div className={`${classes.container} ${props.className}`}>
      <div
        className={`${getDropZoneClassName()} ${
          props.error ? classes.dropZoneError : ""
        }`}
        onDragOver={dragHandler()}
        onDragEnter={dragHandler(e => {
          setRefCount(refCount => refCount + 1);
        })}
        onDrop={dragHandler(e => {
          setRefCount(0);
          const files = props.acceptMultiple
            ? e.dataTransfer.files
            : [e.dataTransfer.files[0]];
          fileChanged(files);
        })}
        onDragLeave={dragHandler(() => {
          setRefCount(refCount => refCount - 1);
        })}
      >
        <div className={classes.selectFileRow}>
          <input
            className={classes.visuallyHidden}
            ref={fileInput => setFileInput(fileInput)}
            type="file"
            accept={props.accept}
            multiple={props.acceptMultiple}
            onChange={e => fileChanged(e.currentTarget.files)}
            aria-labelledby={`${props.labelId} ${props.buttonId}`}
            aria-invalid={props.error ? "true" : "false"}
            aria-describedby={props.errorId}
          />
          <Button
            id={props.buttonId}
            aria-hidden="true"
            tabIndex="-1"
            classes={{ root: classes.button, label: classes.buttonLabel }}
            onClick={() => fileInput.click()}
            variant="text"
            color="secondary"
          >
            <div>
              <CloudUploadIcon />
            </div>
            <div>{props.text}</div>
            {notBlank(props.fileTypes) && (
              <div className={classes.fileTypes}>{props.fileTypes}</div>
            )}
          </Button>
        </div>

        {notBlank(props.files) &&
          Array.from(props.files).map(
            (value, index) =>
              notBlank(value) && (
                <FileRow
                  key={value.name}
                  file={value}
                  fileNumber={index + 1}
                  fileRemoved={fileRemoved}
                />
              )
          )}
      </div>

      <Typography id={props.errorId}>
        {props.error && (
          <span className={classes.errorLabel}>{props.error}</span>
        )}
      </Typography>
    </div>
  );
};

export default FileUploadInput;
